import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input, UpdateStateIndicator } from "@digital-services/ui";
import { isNotEmpty, maxLength, useInput } from "@ds-cps/ui";

import { useInviteFormContext } from "@user/context";

interface Props {
  label: string;
  placeholder: string;
  value: string | undefined;
  onInputChange?: (newValue: string, isValid: boolean) => void;
}

export const DisplayNameAdvancedField: FunctionComponent<Props> = props => {
  const { label, value, placeholder, onInputChange } = props;

  const [isUpdated, setIsUpdated] = useState(false);

  const [displayNameFromServer, setDisplayNameFromServer] = useState("");
  const [disableNameField, setDisableNameField] = useState(true);

  const { inviteInfoObj } = useInviteFormContext();

  const { t } = useTranslation();

  const {
    value: displayName,
    isValid,
    errorMessage,
    valueChangeHandler
  } = useInput(
    [
      maxLength(256, () =>
        t("validation.string.max-length", {
          fieldName: label,
          maxLength: 256
        })
      ),
      isNotEmpty(() => t("validation.string.not-empty", { fieldName: label }))
    ],
    value
  );

  useEffect(() => {
    if (inviteInfoObj.displayName) {
      // So that to be able to pass "" empty value to clear/reset field
      setDisplayNameFromServer(inviteInfoObj.displayName);
      setDisableNameField(true);
      setIsUpdated(true);
    } else if (inviteInfoObj.displayName === "") {
      setDisplayNameFromServer("");
      setDisableNameField(false);
    }
  }, [inviteInfoObj]);

  const endInputAdornment = isUpdated ? (
    <UpdateStateIndicator
      isUpdating={false}
      isUpdated={isUpdated}
      setIsUpdated={setIsUpdated}
    />
  ) : null;

  return (
    <Input
      data-testid="display-name-advanced"
      id="displayNameAdvanced"
      type="text"
      label={label}
      placeholder={placeholder}
      error={!isValid}
      helperText={isValid ? undefined : errorMessage}
      value={displayNameFromServer || displayName}
      disabled={disableNameField}
      endInputAdornment={endInputAdornment}
      onChange={event => {
        const { value, isValid } = valueChangeHandler(event.target.value);
        if (onInputChange) onInputChange(value, isValid);
      }}
    />
  );
};
